import {createSlice, PayloadAction} from '@reduxjs/toolkit';
export interface ISettingsSliceState {
    selectedWorkspaceId?: string;
    /**
     * Bundle to show in the app-level bundle editor. Bundles have no route of their own, so this is
     * how something outside the React tree (a completion toast) can ask for one to be opened.
     * See BundleModalHost.
     */
    openBundleId?: string;
    selectedOrganization?: string;
    /**
     * Sub-organization (customer) the whole app is scoped to, sent as `x-suborganization` on every
     * request. Undefined means "the whole organization". Cleared automatically when the selected
     * organization changes - a sub-org id only means something under its parent.
     */
    selectedSubOrganization?: string;
    loggingOut: boolean;
    loggingOutError?: string;
    punchoutWorkspace?: string;
    effectiveClaims?: string[];
    availableClaims?: {[key: string]: string;};
    externalQuoteId?: string;
    externalQuotePartitionId?: string;
    punchoutMode?: string;
    mappedExtensionFields?: {[key: string]: string;};
    targetExtension?: string;
    refreshSource: boolean
    sourceInstance?: string;
    sessionRefreshCode?: string;
    dismissedNotification?: boolean;
    /**
     * Last instance the user worked with per entity type, so a generic settings deep link can
     * pre-select something sensible. Keyed `${organization}:${instanceType}` - an instance from
     * another tenant is never a useful suggestion.
     */
    lastUsedInstances?: {[key: string]: string};
}

const initialState: ISettingsSliceState = {
    loggingOut: false,
    refreshSource: false
}


const settingsSlice = createSlice({
    name: 'settingsSlice',
    initialState,
    reducers: {
        setSelectedWorkspaceId: (state, action: PayloadAction<string | undefined>) => {
            state.selectedWorkspaceId = action.payload;
        },
        setOpenBundleId: (state, action: PayloadAction<string | undefined>) => {
            state.openBundleId = action.payload;
        },
        setPunchoutWorkspace: (state, action: PayloadAction<string | undefined>) => {
            state.punchoutWorkspace = action.payload;
        },
        setSelectedOrganization: (state, action:PayloadAction<string | undefined>) => {
            if (state.selectedOrganization !== action.payload) {
                state.selectedSubOrganization = undefined;
            }
            state.selectedOrganization = action.payload;
        },
        setSelectedSubOrganization: (state, action:PayloadAction<string | undefined>) => {
            state.selectedSubOrganization = action.payload;
        },
        updateLoggingOut: (state, action: PayloadAction<boolean>) => {
            state.loggingOut = action.payload;
        },
        updateLoggingOutError: (state, action: PayloadAction<string>) => {
            state.loggingOutError = action.payload;
        },
        clearSettings: (state) => {
            state.selectedWorkspaceId = undefined;
            state.openBundleId = undefined;
        },
        setEffectivePermissions: (state, action: PayloadAction<string[] | undefined>) => {
            state.effectiveClaims = action.payload;
        },
        setAvailablePermissions: (state, action: PayloadAction<{[key: string]: string;} | undefined>) => {
            state.availableClaims = action.payload;
        },
        setExternalQuoteId: (state, action: PayloadAction<string | undefined>) => {
            state.externalQuoteId = action.payload;
        },
        setExternalQuotePartitionId: (state, action: PayloadAction<string | undefined>) => {
            state.externalQuotePartitionId = action.payload;
        },
        setPunchoutMode: (state, action: PayloadAction<string | undefined>) => {
            state.punchoutMode = action.payload;
        },
        setTargetExtension: (state, action: PayloadAction<string | undefined>) => {
            state.targetExtension = action.payload;
        },
        setMappedExtensionFields: (state, action: PayloadAction<{[key: string]: string;} | undefined>) => {
            state.mappedExtensionFields = action.payload;
        },
        setRefreshSource: (state, action: PayloadAction<boolean>) => {
            state.refreshSource = action.payload;
        },
        setSourceInstance: (state, action: PayloadAction<string>) => {
            state.sourceInstance = action.payload;
        },
        setSessionRefreshCode: (state, action: PayloadAction<string>) => {
            state.sessionRefreshCode = action.payload;
        },
        
        setDismissedNotification: (state) => {
            state.dismissedNotification = true;
        },
        setLastUsedInstance: (state, action: PayloadAction<{organization?: string, instanceType: string, id: string}>) => {
            const {organization, instanceType, id} = action.payload;
            state.lastUsedInstances = {
                ...state.lastUsedInstances,
                [`${organization ?? ''}:${instanceType}`]: id,
            };
        },
    }
});

export const {
    setSelectedOrganization,
    setSelectedSubOrganization,
    setPunchoutWorkspace,
    updateLoggingOut,
    updateLoggingOutError,
    setSelectedWorkspaceId,
    setOpenBundleId,
    clearSettings,
    setEffectivePermissions,
    setAvailablePermissions,
    setExternalQuotePartitionId,
    setExternalQuoteId,
    setSourceInstance,
    setPunchoutMode,
    setMappedExtensionFields,
    setTargetExtension,
    setRefreshSource,
    setSessionRefreshCode,
    setDismissedNotification,
    setLastUsedInstance
} = settingsSlice.actions;


export default settingsSlice.reducer;